import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Calculator, Moon, Plus, Sun, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { AppShell } from "@/components/AppShell";
import { SystemHealthCard } from "@/components/SystemHealthCard";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

const EMPTY = { name: "", startTime: "10:00", endTime: "22:00" };

/* Admin settings: shift time ranges. Multiple day/night shifts are allowed
   (e.g. Day 10:00–22:00, Night 22:00–10:00, Supervisor Day 07:00–19:00).
   Overnight ranges are supported. Punches auto-detect the nearest shift. */
export default function AdminShiftsPage() {
  const { t } = useLang();
  const { user: me } = useAuth();
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [recalcOpen, setRecalcOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["shifts"],
    queryFn: async () => (await api.get("/shifts")).data,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["shifts"] });

  const saveMutation = useMutation({
    mutationFn: async () => (editing
      ? (await api.patch(`/shifts/${editing.id}`, form)).data
      : (await api.post("/shifts", form)).data),
    onSuccess: () => { toast.success(t("save")); setFormOpen(false); invalidate(); },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const toggleMutation = useMutation({
    mutationFn: async (s) => (await api.patch(`/shifts/${s.id}`, { isActive: !s.isActive })).data,
    onSuccess: invalidate,
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const deleteMutation = useMutation({
    // id passed at click time — AlertDialogAction closes the dialog and nulls
    // deleteTarget before the mutation function runs (same race as user delete)
    mutationFn: async (id) => (await api.delete(`/shifts/${id}`)).data,
    onSuccess: () => { toast.success(t("shiftDeleted")); setDeleteTarget(null); invalidate(); },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const openCreate = () => { setEditing(null); setForm(EMPTY); setFormOpen(true); };

  // Admin-only healing tool: re-checks every stored punch against current
  // shifts/assignments and fixes wrong late/early values (Batch 11)
  const recalcMutation = useMutation({
    mutationFn: async () => (await api.post("/admin/recalc-punctuality")).data,
    onSuccess: (d) => {
      toast.success(`${d.updated} ${t("recalcDone")} (${d.scanned} ${t("recalcScanned")})`);
      setRecalcOpen(false);
      queryClient.invalidateQueries();
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, startTime: s.startTime, endTime: s.endTime });
    setFormOpen(true);
  };

  return (
    <AppShell>
      <div className="space-y-6">
        <SystemHealthCard />
        <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{t("shifts")}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t("shiftsHelp")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {me?.role === "admin" && (
              <Button variant="outline" onClick={() => setRecalcOpen(true)}
                data-testid="recalc-punctuality-button">
                <Calculator className="mr-2 h-4 w-4" /> {t("recalcButton")}
              </Button>
            )}
            <Button onClick={openCreate} data-testid="create-shift-button">
              <Plus className="mr-2 h-4 w-4" /> {t("createShift")}
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table data-testid="shifts-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("shiftName")}</TableHead>
                <TableHead>{t("timeRange")}</TableHead>
                <TableHead>{t("shiftKind")}</TableHead>
                <TableHead>{t("status")}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">{t("loading")}</TableCell></TableRow>
              )}
              {!isLoading && (data?.items || []).length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground" data-testid="shifts-empty">{t("noShifts")}</TableCell></TableRow>
              )}
              {data?.items?.map((s) => (
                <TableRow key={s.id} data-testid="shift-row">
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="font-mono tabular-nums">
                    {s.startTime} – {s.endTime}{s.overnight ? " (+1d)" : ""}
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                      {s.kind === "day"
                        ? <><Sun className="h-3.5 w-3.5 text-amber-600" /> {t("dayKind")}</>
                        : <><Moon className="h-3.5 w-3.5 text-indigo-600" /> {t("nightKind")}</>}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={s.isActive} onCheckedChange={() => toggleMutation.mutate(s)}
                        data-testid="shift-active-switch" />
                      <Badge variant={s.isActive ? "secondary" : "outline"}>
                        {s.isActive ? t("active") : t("inactive")}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid="shift-edit-button">
                      {t("editUser").split(" ")[0]}
                    </Button>
                    <Button size="icon" variant="ghost" className="ml-1 text-destructive"
                      onClick={() => setDeleteTarget(s)} data-testid="shift-delete-button">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        </div>
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="shift-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t("editShift") : t("createShift")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t("shiftName")}</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="shift-form-name" placeholder="Day Shift" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("startTime")}</Label>
                <Input type="time" value={form.startTime}
                  onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
                  data-testid="shift-form-start" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("endTime")}</Label>
                <Input type="time" value={form.endTime}
                  onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
                  data-testid="shift-form-end" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t("overnightHint")}</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFormOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="shift-form-save" disabled={!form.name.trim() || !form.startTime || !form.endTime || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}>
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteTarget !== null} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-shift-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteShift")} — {deleteTarget?.name}</AlertDialogTitle>
            <AlertDialogDescription>{t("deleteShiftConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-shift-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteMutation.mutate(deleteTarget.id)}>
              {t("deleteShift")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={recalcOpen} onOpenChange={setRecalcOpen}>
        <AlertDialogContent data-testid="recalc-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("recalcTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("recalcBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="recalc-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="recalc-confirm"
              disabled={recalcMutation.isPending}
              onClick={(e) => { e.preventDefault(); recalcMutation.mutate(); }}>
              {recalcMutation.isPending ? "…" : t("recalcButton")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
